// import { Subjectivity } from '../../models/quote.model'

export enum QuoteIngestionStatus {
  Pending = 'PENDING',
  Accepted = 'ACCEPTED',
  Rejected = 'REJECTED',
}

export interface IngestedEmailIdAndStatus {
  id: number
  status: QuoteIngestionStatus
}

/**
 * equivalent to `AIEmailResponseDto` type on backend
 */
export interface IngestedEmail {
  id: number
  fromAddresses: string
  toAddresses: string
  ccAddresses: string | null
  subject: string
  body: string
  auditInsertDate: string
  structureDetail: StructureDetail
  quoteFieldChanges?: QuoteFieldChanges
  pendingQuotes: PendingQuote[]
  status: QuoteIngestionStatus
}

export interface StructureDetail {
  structureId: number
  structureName: string
  programId: number
  programName: string
  carrierId: number
  carrierName: string
  year: number
  inceptionDate: string
}

/**
 * Outer Key: structure id
 * Inner Key: layer ref
 */
export type QuoteFieldChanges = Record<string, Record<string, FieldChangeDetail[]>>

export interface FieldChangeDetail {
  fieldName: string
  originalValueStr: string
}

/**
 * received from backend via `/ai-quote-responses` endpoint within `IngestedEmail` type
 */
export interface PendingQuote {
  id: number
  pendingQuoteId: string
  layerRef: string
  layerType: string
  marketTpref: string
  structureId: number | null
  layerName: string
  riskSectionPhaseLabel: string
  occurrenceLimit: number | null
  occurrenceAttachment: number | null
  rolPercentage: number | null
  rateOnLineSubject: number | null
  premium: number | null
  status: string
  reinstatement: string
  emailDate: string
  emailId: number
  carrierId: number | null
  programId: number | null
  subjectPremium: number | null
  minimumPremium: number | null
  quoteDepositPremium: number | null
  minimumPremiumPercentage: number | null
  depositPremiumPercentage: number | null
  offeredPercentage: number | null
  brokerageCommission: number | null
  brokerageRIPCommission: number | null
  cedingComm: number | null
  reinsurerName: string
  subjectivities: PendingSubjectivity[]
  existingQuoteWarnings?: ExistingQuoteWarning[]
  wasCalculated?: {
    premium: boolean
    subjectPremium: boolean
  }
}

export interface ExistingQuoteWarning {
  riskReinsurerId: number
  layerRef: string
  riskSectionPhaseLabel: string
  rolPercentage: number | null
  rateOnLineSubject: number | null
  cedingCommission: number | null
  premium: number | null
  reinstatements?: ExistingReinstatement[] | null
}

export interface ExistingReinstatement {
  ripCount: number | null
  ripPct: number | null
  ripFreeInd: boolean
}

export interface PendingSubjectivity {
  id: number | null
  pendingQuoteId: string
  acceptedContractWord: string
  acceptedLineCond: string
  brokerComments: string
  clientAccepted: string
  deadline: string
  marketTpRef: string
  ralId: number | null
  reinsCommentary: string
  reinsResponseStatus: string
  reinsurer: string
  reinsurerResponse: string
  riskRef: string
  riskReinsurerId: number | null
  type: string
  vendorIdentifier: string
  applyToStructureForSameMarket: boolean
  vendorName: string
  layerId: string
}

/**
 * Editable numeric fields in PendingQuote that require type conversion from string inputs
 */
export const PENDING_QUOTE_NUMERIC_FIELDS: readonly (keyof PendingQuote)[] = [
  'occurrenceLimit',
  'occurrenceAttachment',
  'rolPercentage',
  'rateOnLineSubject',
  'premium',
  'subjectPremium',
  'minimumPremium',
  'quoteDepositPremium',
  'minimumPremiumPercentage',
  'depositPremiumPercentage',
  'offeredPercentage',
  'brokerageCommission',
  'brokerageRIPCommission',
  'cedingComm',
] as const

export const PENDING_QUOTES_READONLY_FIELDS: readonly (keyof PendingQuote)[] = [
  'premium',
  'subjectPremium',
] as const

/**
 * sent to backend via `/accept-ingestion` endpoint
 */
export interface PendingQuoteDto {
  pendingQuoteId: string
  id: number | null
  layerRef: string
  layerType: string
  carrier: string
  year: string
  program: string
  marketTpref: string
  reinsurer: string
  structureId: number
  layerName: string
  quoteVersion: string
  occurrenceLimit: number | null
  occurrenceAttachment: number | null
  rolPercentage: number | null
  rateOnLineSubject: number | null
  premium: number | null
  subjectivities: PendingSubjectivityDto[]
  status: string
  inceptionDate: string
  reinstatement: string
  emailDate: string
  emailId: number
  carrierId: number | null
  programId: number | null
  subjectPremium: number | null
  minimumPremium: number | null
  quoteDepositPremium: number | null
  minimumPremiumPercentage: number | null
  depositPremiumPercentage: number | null
  offeredPercentage: number | null
  brokerageCommission: number | null
  brokerageRIPCommission: number | null
  cedingComm: number | null
}

export interface PendingSubjectivityDto {
  id: number | null
  pendingQuoteId: string
  acceptedContractWord: string
  acceptedLineCond: string
  brokerComments: string
  clientAccepted: string
  deadline: string
  marketTpRef: string
  ralId: number | null
  reinsCommentary: string
  reinsResponseStatus: string
  reinsurer: string
  reinsurerResponse: string
  riskRef: string
  riskReinsurerId: number | null
  type: string
  vendorIdentifier: string
  applyToStructureForSameMarket: boolean
  vendorName: string
  layerId: string
}

export interface QuoteStats {
  total: number
  pending: number
  accepted: number
  rejected: number
}

export interface FilterValues {
  status: QuoteIngestionStatus
  clients: string[]
  years: string[]
  programs: string[]
  markets: string[]
  brokers: string[]
  inceptionDateStart: string
  inceptionDateEnd: string
  emailDateStart: string
  emailDateEnd: string
}

export type ViewType = 'card' | 'table'

export type SortColumn =
  | 'client'
  | 'year'
  | 'program'
  | 'broker'
  | 'market'
  | 'structure'
  | 'received'
  | null

export type SortDirection = 'asc' | 'desc' | 'off'

export const DEFAULT_FILTERS: FilterValues = {
  status: QuoteIngestionStatus.Pending,
  clients: [],
  years: [],
  programs: [],
  markets: [],
  brokers: [],
  inceptionDateStart: '',
  inceptionDateEnd: '',
  emailDateStart: '',
  emailDateEnd: '',
}

export const DEFAULT_STATS: QuoteStats = {
  total: 0,
  pending: 0,
  accepted: 0,
  rejected: 0,
}
